import { classNames, useEffect, useRef, useState } from 'libs/react';
import { React } from 'libs/react';
import { SaleItemCard } from './components/StoreSaleItemCard';
import { StoreStepContentHeader } from './components/StoreStepContentHeader';
import { StorePriceSummary } from './components/StorePriceSummary';
import { FormInput } from 'components/form/FormInput';
import { useDomain } from 'components/domain/DomainProvider';
import { useHistory } from 'react-router-dom';
import { StoreSaleItemCardSkeleton } from './components/StoreSaleItemCardSkeleton';
import { buildStoreCategories, saleItemCategoryTitle, STORE_CATEGORY_NAV_MIN, storeCategorySearch } from './storeCategories';
import { buildOptionsPayload, createSelectedItem, getIncludedOptions, getPanelOptions, getProposedOptions, toggleSelectedOption } from './StoreProposedOptions';
import './StoreProduct.scss';
/**
 * The ?product= product is selected once per page load, so the buyer can
 * remove it. Kept outside the component: the section remounts each time the
 * buyer comes back to the products step.
 */
let preselectedProductKey = null;
/**
 * Placeholders shown while the catalog loads: two rows on a desktop, enough
 * to fill a phone screen.
 * @const SKELETON_KEYS
 * @hidden
 */
const SKELETON_KEYS = ['a', 'b', 'c', 'd', 'e', 'f'];
/**
 * @component ProductSection
 * @since 1.0.0
 */
export function ProductSection(props) {
    var _a, _b, _c, _d, _e, _f, _g;
    const { data, hasDirectSales, lang, onNext, onSelectionChange, ready, saleItemGroup, showFormInputs = true, showProposedOptions = false, selectedItems: controlledSelectedItems, disableAutoSelect = false, showCategoryNav = false } = props;
    const { options } = useDomain();
    const history = useHistory();
    const [internalSelectedItems, setInternalSelectedItems] = useState(controlledSelectedItems !== null && controlledSelectedItems !== void 0 ? controlledSelectedItems : []);
    const selectedItems = controlledSelectedItems !== null && controlledSelectedItems !== void 0 ? controlledSelectedItems : internalSelectedItems;
    const [saleItems, setSaleItems] = useState((data === null || data === void 0 ? void 0 : data.saleItems) || []);
    const autoSelectedRef = useRef(null);
    const selectedItemsRef = useRef(selectedItems);
    selectedItemsRef.current = selectedItems;
    const queryParameters = new URLSearchParams(window.location.search);
    const product = (_a = queryParameters.get('product')) === null || _a === void 0 ? void 0 : _a.toLowerCase();
    const category = queryParameters.get('category');
    const catalogKey = (_c = (_b = data === null || data === void 0 ? void 0 : data.saleItems) === null || _b === void 0 ? void 0 : _b.map((entry) => entry.id).join(',')) !== null && _c !== void 0 ? _c : '';
    const categories = showCategoryNav
        ? buildStoreCategories((_d = data === null || data === void 0 ? void 0 : data.saleItems) !== null && _d !== void 0 ? _d : [], saleItemGroup, lang, category)
        : [];
    const activeCategory = (_e = categories.find((entry) => entry.id === category)) !== null && _e !== void 0 ? _e : null;
    // A ?product= link without category shows that product alone.
    const collapsedToProduct = !!product &&
        !category &&
        (options.selectionMode === 'single' ||
            options.allowSingleFallback === 'TRUE') &&
        !!((_f = data === null || data === void 0 ? void 0 : data.saleItems) === null || _f === void 0 ? void 0 : _f.some((entry) => entry.sku.toLowerCase().trim() === product));
    const showCategories = categories.length >= STORE_CATEGORY_NAV_MIN && !collapsedToProduct;
    const onCategoryChange = (categoryId) => {
        history.replace({
            pathname: window.location.pathname,
            search: storeCategorySearch(window.location.search, categoryId)
        });
    };
    useEffect(() => {
        var _a, _b;
        if (saleItems.length > 0) {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event: 'view_item_list',
                ecommerce: {
                    item_list_name: (_a = activeCategory === null || activeCategory === void 0 ? void 0 : activeCategory.title) !== null && _a !== void 0 ? _a : 'saleItems',
                    item_list_id: (_b = activeCategory === null || activeCategory === void 0 ? void 0 : activeCategory.id) !== null && _b !== void 0 ? _b : 'saleItems',
                    items: saleItems.map((saleItem) => ({
                        currency: 'CAD',
                        item_id: saleItem.sku,
                        item_name: saleItem.title,
                        item_category: saleItemCategoryTitle(saleItem, lang),
                        price: saleItem.price || 0,
                        quantity: 1
                    }))
                }
            });
        }
    }, [saleItems, options.gtmId]);
    /**
     * @function updateFinishedProductsFlag
     * @since 1.0.0
     * @hidden
     */
    function updateFinishedProductsFlag(items, itemsSource = saleItems) {
        if (items.length > 0) {
            props.setHasOnlyFinishedProducts(itemsSource
                .filter((saleItem) => items.some((item) => item.id === saleItem.id))
                .every((saleItem) => saleItem.configuration === 'FINISHED'));
        }
        else {
            props.setHasOnlyFinishedProducts(false);
        }
    }
    /**
     * @function applySelection
     * @since 1.0.0
     * @hidden
     */
    function applySelection(nextSelectedItems, nextSaleItems = saleItems) {
        if (controlledSelectedItems == null) {
            setInternalSelectedItems(nextSelectedItems);
        }
        updateFinishedProductsFlag(nextSelectedItems, nextSaleItems);
        onSelectionChange === null || onSelectionChange === void 0 ? void 0 : onSelectionChange(nextSelectedItems);
    }
    /**
     * @function autoSelectSingleItem
     * @since 1.0.0
     * @hidden
     */
    function autoSelectSingleItem(item) {
        const syncKey = `${catalogKey}:${item.id}`;
        if (autoSelectedRef.current === syncKey) {
            setSaleItems((current) => current.length === 1 && current[0].id === item.id ? current : [item]);
            return;
        }
        autoSelectedRef.current = syncKey;
        setSaleItems([item]);
        applySelection([createSelectedItem(item, 1)], [item]);
    }
    /**
     * @function onSaleItemCardClick
     * @since 1.0.0
     */
    const onSaleItemCardClick = (id, quantity) => {
        const index = selectedItems.findIndex((item) => item.id === id);
        const saleItem = saleItems.find((entry) => entry.id === id);
        let newSelectedItems;
        if (options.selectionMode === 'single') {
            if (index === -1 && quantity > 0) {
                newSelectedItems = saleItem
                    ? [createSelectedItem(saleItem, quantity)]
                    : [{ id, quantity, selectedOptionIds: [] }];
            }
            else {
                newSelectedItems = [];
            }
        }
        else if (index === -1 && quantity > 0) {
            newSelectedItems = [
                ...selectedItems,
                saleItem
                    ? createSelectedItem(saleItem, quantity)
                    : { id, quantity, selectedOptionIds: [] }
            ];
        }
        else {
            newSelectedItems = selectedItems.filter((item) => item.id !== id);
        }
        if (newSelectedItems.length > 0 &&
            (index === -1 || options.selectionMode === 'single') &&
            quantity > 0) {
            window.dataLayer = window.dataLayer || [];
            const item = saleItems.find((entry) => entry.id === id);
            const price = (item === null || item === void 0 ? void 0 : item.price) || 0;
            const gtmItems = [
                {
                    currency: 'CAD',
                    item_id: (item === null || item === void 0 ? void 0 : item.sku) || id,
                    item_name: (item === null || item === void 0 ? void 0 : item.title) || '',
                    item_category: saleItemCategoryTitle(item, lang),
                    price,
                    quantity
                }
            ];
            for (const eventName of ['view_item', 'select_item', 'add_to_cart']) {
                window.dataLayer.push({
                    event: eventName,
                    ecommerce: {
                        currency: 'CAD',
                        value: price * quantity,
                        items: gtmItems
                    }
                });
            }
        }
        autoSelectedRef.current = null;
        applySelection(newSelectedItems);
    };
    /**
     * @function onSaleItemQuantityChange
     * @since 1.0.0
     */
    const onSaleItemQuantityChange = (id, quantity) => {
        applySelection(selectedItems.map((item) => item.id === id ? Object.assign(Object.assign({}, item), { quantity }) : item));
    };
    /**
     * @function onOptionChange
     * @since 1.0.0
     */
    const onOptionChange = (productId, optionItemId, checked) => {
        applySelection(selectedItems.map((selectedItem) => {
            if (selectedItem.id !== productId) {
                return selectedItem;
            }
            const saleItem = saleItems.find((entry) => entry.id === productId);
            if (!saleItem) {
                return selectedItem;
            }
            return toggleSelectedOption(saleItem, selectedItem, optionItemId, checked);
        }));
    };
    useEffect(() => {
        if (!(data === null || data === void 0 ? void 0 : data.saleItems)) {
            return;
        }
        // ?category= replaces the configured default group, including when
        // ?product= preselects one product of that category.
        const categoryItems = category
            ? data.saleItems.filter((entry) => {
                var _a, _b;
                return ((_a = entry.group) === null || _a === void 0 ? void 0 : _a.id) === category ||
                    ((_b = entry.groups) === null || _b === void 0 ? void 0 : _b.some((group) => group.id === category));
            })
            : null;
        if (product) {
            const saleItem = data.saleItems.find((entry) => entry.sku.toLowerCase().trim() === product);
            if (saleItem) {
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                    event: 'view_item',
                    ecommerce: {
                        currency: 'CAD',
                        value: saleItem.price || 0,
                        items: [
                            {
                                currency: 'CAD',
                                item_id: saleItem.sku,
                                item_name: saleItem.title,
                                price: saleItem.price || 0,
                                quantity: 1
                            }
                        ]
                    }
                });
                const preselectKey = `${window.location.pathname}|${product}|${category !== null && category !== void 0 ? category : ''}`;
                const alreadyPreselected = preselectedProductKey === preselectKey;
                // Without a category, a single-selection store still shows only
                // the product passed in the URL.
                const collapseToProduct = categoryItems == null &&
                    (options.selectionMode === 'single' ||
                        options.allowSingleFallback === 'TRUE');
                if (collapseToProduct) {
                    if (alreadyPreselected) {
                        setSaleItems((current) => current.length === 1 && current[0].id === saleItem.id
                            ? current
                            : [saleItem]);
                        return;
                    }
                    preselectedProductKey = preselectKey;
                    autoSelectSingleItem(saleItem);
                    return;
                }
                const filteredItems = categoryItems !== null && categoryItems !== void 0 ? categoryItems : (saleItemGroup
                    ? data.saleItems.filter((entry) => {
                        var _a, _b, _c;
                        return (((_a = entry.group) === null || _a === void 0 ? void 0 : _a.id) &&
                            saleItemGroup.includes((_b = entry.group) === null || _b === void 0 ? void 0 : _b.id)) ||
                            ((_c = entry.groups) === null || _c === void 0 ? void 0 : _c.some((group) => saleItemGroup.includes(group.id)));
                    })
                    : data.saleItems);
                const productInList = categoryItems == null ||
                    filteredItems.some((entry) => entry.id === saleItem.id);
                const currentSelection = selectedItemsRef.current;
                const alreadySelected = currentSelection.some((item) => item.id === saleItem.id);
                setSaleItems(filteredItems);
                if (!productInList || alreadyPreselected) {
                    return;
                }
                preselectedProductKey = preselectKey;
                if (options.selectionMode === 'single') {
                    const onlyThisProduct = currentSelection.length === 1 &&
                        currentSelection[0].id === saleItem.id;
                    if (!onlyThisProduct) {
                        applySelection([createSelectedItem(saleItem, 1)], filteredItems);
                    }
                    return;
                }
                if (alreadySelected) {
                    return;
                }
                applySelection([...currentSelection, createSelectedItem(saleItem, 1)], filteredItems);
                return;
            }
        }
        if (categoryItems != null) {
            setSaleItems(categoryItems);
        }
        else if (saleItemGroup) {
            const filteredItems = data.saleItems.filter((entry) => {
                var _a, _b, _c;
                return (((_a = entry.group) === null || _a === void 0 ? void 0 : _a.id) && saleItemGroup.includes((_b = entry.group) === null || _b === void 0 ? void 0 : _b.id)) ||
                    ((_c = entry.groups) === null || _c === void 0 ? void 0 : _c.some((group) => saleItemGroup.includes(group.id)));
            });
            if (filteredItems.length === 1 && !disableAutoSelect) {
                autoSelectSingleItem(filteredItems[0]);
            }
            else {
                setSaleItems(filteredItems);
            }
        }
        else if (!product && !saleItemGroup) {
            setSaleItems(data.saleItems);
        }
    }, [saleItemGroup, product, category, catalogKey, disableAutoSelect]);
    const className = classNames('section store-product-section');
    const backgroundImage = (_g = options.backgroundImage) !== null && _g !== void 0 ? _g : null;
    const showSectionTitle = options.selectionMode !== 'single' || !!category || !product;
    return (React.createElement("div", { className: className, style: {
            backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none'
        } },
        showSectionTitle && (React.createElement(StoreStepContentHeader, { backgroundImage: backgroundImage })),
        React.createElement("div", { className: "store-product-section__content", "aria-busy": !ready }, ready ? (saleItems.map((saleItem) => {
            var _a;
            const selectedItem = selectedItems.find((item) => item.id === saleItem.id);
            const selected = selectedItem != null;
            const includedOptions = getIncludedOptions(saleItem);
            const proposedOptions = getProposedOptions(saleItem);
            const panelOptions = getPanelOptions(saleItem);
            const showOptionsPanel = includedOptions.length > 0 ||
                (showProposedOptions && proposedOptions.length > 0);
            return (React.createElement(React.Fragment, { key: saleItem.id },
                React.createElement(SaleItemCard, { item: saleItem, onClick: onSaleItemCardClick, onQuantityChange: onSaleItemQuantityChange, selected: selected, lang: lang, hasDirectSales: hasDirectSales, selectedAccent: "primary", uncheckedToggleLabel: "add", selectedOptionCount: (_a = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.selectedOptionIds.length) !== null && _a !== void 0 ? _a : 0 })));
        })) : (SKELETON_KEYS.map((key) => React.createElement(StoreSaleItemCardSkeleton, { key: key })))),
        selectedItems.length > 0 && (React.createElement(StorePriceSummary, { lang: lang, saleItems: saleItems, selectedItems: selectedItems, onNext: onNext })),
        showFormInputs && (React.createElement(React.Fragment, null,
            React.createElement(FormInput, { type: "hidden", name: "saleItems", value: selectedItems
                    .map((item) => `${item.id}:${item.quantity}`)
                    .join(',') }),
            React.createElement(FormInput, { type: "hidden", name: "saleItemOptions", value: JSON.stringify(buildOptionsPayload(selectedItems, saleItems)) })))));
}
